import { useState, useEffect, useRef, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TrendingUp, Users, Globe2, ArrowLeft, ArrowRight, Check, Languages, Menu, X,
  LineChart, PieChart, Bell, Target, Layers, ArrowUp, ShoppingBag, Megaphone,
  Smartphone, Database, Plus,
} from 'lucide-react';
import MarketingFooter from '../components/marketing/MarketingFooter';
import ryzeLogo from '../assets/logo.svg';
import { isRtl } from '../i18n';
import { useLandingMotion } from '../hooks/useLandingMotion';
import { PLAN_PRICES_USD } from '../lib/plans';

type NavLink = { key: string; href: string };

const NAV_LINKS: NavLink[] = [
  { key: 'features',     href: '#features' },
  { key: 'pricing',      href: '/pricing' },
  { key: 'integrations', href: '#integrations' },
  { key: 'faq',          href: '#faq' },
  { key: 'changelog',    href: '#changelog' },
];

const PLANS = [
  {
    key: 'starter',
    price: PLAN_PRICES_USD.starter.monthly,
    featureKeys: ['workspace', 'sources', 'dashboards', 'history', 'support'],
    popular: false,
  },
  {
    key: 'growth',
    price: PLAN_PRICES_USD.growth.monthly,
    featureKeys: ['workspace', 'sources', 'analytics', 'history', 'attribution', 'support'],
    popular: true,
  },
  {
    key: 'scale',
    price: PLAN_PRICES_USD.scale.monthly,
    featureKeys: ['workspaces', 'sources', 'bi', 'history', 'experiments', 'csm'],
    popular: false,
  },
  {
    key: 'enterprise',
    price: PLAN_PRICES_USD.enterprise.monthly,
    featureKeys: ['workspaces', 'sso', 'models', 'sla', 'residency', 'whiteLabel'],
    popular: false,
  },
];

const FEATURES = [
  {
    icon: <Layers className="h-6 w-6" />,
    key: 'unifiedDataLayer',
    featured: true,
  },
  {
    icon: <Users className="h-6 w-6" />,
    key: 'identityResolution',
    featured: true,
  },
  {
    icon: <TrendingUp className="h-6 w-6" />,
    key: 'multiTouchAttribution',
  },
  {
    icon: <PieChart className="h-6 w-6" />,
    key: 'customerIntelligence',
  },
  {
    icon: <LineChart className="h-6 w-6" />,
    key: 'forecastingGoals',
  },
  {
    icon: <Bell className="h-6 w-6" />,
    key: 'realTimeAlerts',
  },
  {
    icon: <Globe2 className="h-6 w-6" />,
    key: 'multiCurrencyRegion',
  },
  {
    icon: <Target className="h-6 w-6" />,
    key: 'abExperiments',
  },
];

function FeatureSignal({
  featureKey,
  signal,
  featured,
}: {
  featureKey: string;
  signal: string;
  featured?: boolean;
}) {
  return (
    <div
      className={`ryze-feature-signal ${featured ? 'ryze-feature-signal--featured' : ''}`}
      data-feature-signal={featureKey}
      aria-hidden="true"
    >
      <div className="ryze-feature-signal__topline">
        <span className="ryze-feature-signal__pulse" />
        <span>{signal}</span>
      </div>
      <div className="ryze-feature-signal__chart">
        <span />
        <span />
        <span />
        <span />
        <span />
      </div>
    </div>
  );
}

const INTEGRATION_GROUPS = [
  {
    key: 'commercePayments',
    icon: <ShoppingBag className="h-4 w-4" />,
    items: [
      { name: 'Shopify', color: '#95BF47' },
      { name: 'Salla', color: '#7B6CF6' },
      { name: 'Zid', color: '#FF6B35' },
      { name: 'Tabby', color: '#3DBDA7' },
      { name: 'Tamara', color: '#FF5F57' },
      { name: 'Stripe', color: '#635BFF' },
    ],
  },
  {
    key: 'growthChannels',
    icon: <Megaphone className="h-4 w-4" />,
    items: [
      { name: 'Meta Ads', color: '#4095FF' },
      { name: 'Google Ads', color: '#EA4335' },
      { name: 'TikTok Ads', color: '#F0EDE6' },
      { name: 'Snapchat Ads', color: '#FFFC00' },
    ],
  },
  {
    key: 'mobileAttribution',
    icon: <Smartphone className="h-4 w-4" />,
    items: [
      { name: 'GA4', color: '#E37400' },
      { name: 'Adjust', color: '#3987FF' },
      { name: 'AppsFlyer', color: '#2EB6C9' },
      { name: 'Branch', color: '#8F78FF' },
    ],
  },
  {
    key: 'dataLifecycle',
    icon: <Database className="h-4 w-4" />,
    items: [
      { name: 'WhatsApp', color: '#25D366' },
      { name: 'Klaviyo', color: '#F0EDE6' },
      { name: 'BigQuery', color: '#4285F4' },
      { name: 'Snowflake', color: '#29B5E8' },
    ],
  },
];

// Honest, product-capability differentiators — replaced fabricated named
// testimonials (no customer has consented to be quoted yet).
const DIFFERENTIATORS = [
  {
    icon: <Layers className="h-6 w-6" />,
    key: 'singleSourceOfTruth',
  },
  {
    icon: <Users className="h-6 w-6" />,
    key: 'trustedAttribution',
  },
  {
    icon: <Globe2 className="h-6 w-6" />,
    key: 'builtForGulf',
  },
];

const HERO_SOURCES = [
  {
    name: 'Shopify',
    key: 'orders',
    schema: 'order_id · value',
    volume: '24.8k',
    color: '#95BF47',
    icon: <ShoppingBag className="h-3.5 w-3.5" />,
  },
  {
    name: 'Meta Ads',
    key: 'adSpend',
    schema: 'campaign · spend',
    volume: '1.2m',
    color: '#4095FF',
    icon: <Megaphone className="h-3.5 w-3.5" />,
  },
  {
    name: 'GA4',
    key: 'sessions',
    schema: 'session · event',
    volume: '3.8m',
    color: '#E37400',
    icon: <LineChart className="h-3.5 w-3.5" />,
  },
  {
    name: 'Mobile app',
    key: 'mobileEvents',
    schema: 'device_id · event',
    volume: '842k',
    color: '#8F78FF',
    icon: <Smartphone className="h-3.5 w-3.5" />,
  },
  {
    name: 'BigQuery',
    key: 'warehouse',
    schema: 'customer · margin',
    volume: '9.6m',
    color: '#4285F4',
    icon: <Database className="h-3.5 w-3.5" />,
  },
];

const HERO_CHANNELS = [
  { name: 'Meta', value: 'SAR 1.7m', width: '82%' },
  { name: 'Google', value: 'SAR 1.2m', width: '64%' },
  { name: 'Organic', value: 'SAR 860k', width: '46%' },
];

const STATS = [
  { valueKey: 'any', key: 'anySource' },
  { value: '5', key: 'attributionModels' },
  { value: '1', key: 'eventStore' },
  { value: 'PDPL', key: 'privacyAligned' },
];

const FAQS = [
  'whatIsRyze',
  'dataSources',
  'menaMarkets',
  'multiTouchAttribution',
];

const CHANGELOG_ENTRIES = [
  { key: 'forecasting', dateTime: '2026-06', version: 'v2.6' },
  { key: 'identity', dateTime: '2026-06', version: 'v2.5' },
  { key: 'attribution', dateTime: '2026-05', version: 'v2.4' },
];

export default function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<string | null>(FAQS[0]);
  const [scrolled, setScrolled] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const motionScope = useRef<HTMLDivElement>(null);
  const scrollProgressRef = useRef<HTMLDivElement>(null);
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? i18n.language;
  const rtl = isRtl(language);
  const DirectionalArrow = rtl ? ArrowLeft : ArrowRight;

  useLandingMotion({ language, rtl, scope: motionScope });

  useEffect(() => {
    let animationFrame: number | null = null;

    const updateScrollUi = () => {
      animationFrame = null;
      const scrollTop = window.scrollY;
      const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = scrollableHeight > 0
        ? Math.min(Math.max(scrollTop / scrollableHeight, 0), 1)
        : 0;

      if (scrollProgressRef.current) {
        scrollProgressRef.current.style.transform = `scaleX(${progress})`;
      }

      setScrolled(scrollTop > 20);
      setShowBackToTop(scrollTop > 600);
    };

    const requestUpdate = () => {
      if (animationFrame === null) {
        animationFrame = window.requestAnimationFrame(updateScrollUi);
      }
    };

    updateScrollUi();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);

    return () => {
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
      if (animationFrame !== null) {
        window.cancelAnimationFrame(animationFrame);
      }
    };
  }, []);

  const scrollToTop = () => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
  };

  useEffect(() => {
    const title = t('landing.seo.title');
    const description = t('landing.seo.description');
    document.title = title;
    document.head.querySelector<HTMLMetaElement>('meta[name="description"]')?.setAttribute('content', description);
    document.head.querySelector<HTMLMetaElement>('meta[property="og:title"]')?.setAttribute('content', title);
    document.head.querySelector<HTMLMetaElement>('meta[property="og:description"]')?.setAttribute('content', description);
    document.head.querySelector<HTMLMetaElement>('meta[name="twitter:title"]')?.setAttribute('content', title);
    document.head.querySelector<HTMLMetaElement>('meta[name="twitter:description"]')?.setAttribute('content', description);
  }, [i18n.language, t]);

  return (
    <div
      ref={motionScope}
      dir={rtl ? 'rtl' : 'ltr'}
      className={`min-h-screen ${rtl ? 'text-right' : 'text-left'}`}
      style={{ background: '#0D0B07', color: '#F0EDE6', fontFamily: rtl ? 'Almarai, var(--app-font-family)' : 'var(--app-font-family)' }}
    >
      <div
        className="fixed inset-x-0 top-0 z-[70] h-[3px] pointer-events-none"
        style={{ background: 'rgba(240,237,230,0.06)' }}
        aria-hidden="true"
      >
        <div
          ref={scrollProgressRef}
          className="h-full w-full will-change-transform"
          style={{
            background: '#A2544F',
            boxShadow: '0 0 12px rgba(162,84,79,0.65)',
            transform: 'scaleX(0)',
            transformOrigin: rtl ? 'right center' : 'left center',
          }}
        />
      </div>

      <a
        href="#main-content"
        className={`sr-only focus:not-sr-only focus:absolute ${rtl ? 'focus:right-4' : 'focus:left-4'} focus:top-4 focus:z-[60] focus:rounded-lg focus:bg-white focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-stone-950`}
      >
        {t('landing.accessibility.skipToContent')}
      </a>
      {/* ── NAV ───────────────────────────────────────────────── */}
      <header
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-300"
        style={{
          background: scrolled ? 'rgba(13,11,7,0.92)' : 'transparent',
          backdropFilter: scrolled ? 'blur(12px)' : 'none',
          borderBottom: scrolled ? '1px solid rgba(240,237,230,0.08)' : 'none',
        }}
      >
        <div className={`${rtl ? 'grid grid-cols-[auto_1fr_auto] gap-3' : 'relative flex justify-between'} mx-auto min-h-16 max-w-7xl items-center px-4 py-3 sm:px-6 sm:py-4`}>
          {/* Logo */}
          <a href="/" className="flex items-center gap-2.5 select-none">
            <img
              src={ryzeLogo}
              alt="Ryze Analytics"
              className="h-12 w-auto"
              style={{ filter: 'invert(1)' }}
            />
          </a>

          {/* Desktop nav */}
          <nav
            className={rtl
              ? 'hidden min-w-0 items-center justify-center gap-4 lg:flex xl:gap-6'
              : 'absolute left-1/2 hidden -translate-x-1/2 items-center gap-6 md:flex'}
            aria-label={t('landing.accessibility.primaryNavigation')}
          >
            {NAV_LINKS.map(l => (
              <a
                key={l.key}
                href={l.href}
                className={rtl ? 'whitespace-nowrap text-sm transition-colors xl:text-base' : 'text-base transition-colors'}
                style={{ color: 'rgba(240,237,230,0.6)' }}
                onMouseEnter={e => (e.currentTarget.style.color = '#F0EDE6')}
                onMouseLeave={e => (e.currentTarget.style.color = 'rgba(240,237,230,0.6)')}
              >
                {t(`landing.nav.${l.key}`)}
              </a>
            ))}
          </nav>

          <div className={rtl ? 'hidden min-w-0 items-center gap-2 lg:flex xl:gap-3' : 'hidden items-center gap-3 md:flex'}>
            <button
              type="button"
              onClick={() => void i18n.changeLanguage(rtl ? 'en' : 'ar')}
              className="ryze-header-action inline-flex items-center gap-2 px-3 py-2 text-base transition-colors"
              style={{ border: '1px solid rgba(240,237,230,0.15)', color: 'rgba(240,237,230,0.8)' }}
              aria-label={t(rtl ? 'common.switchToEnglish' : 'common.switchToArabic')}
              title={t(rtl ? 'common.switchToEnglish' : 'common.switchToArabic')}
            >
              <Languages className="h-4 w-4" aria-hidden="true" />
              <span>{t(rtl ? 'common.english' : 'common.arabic')}</span>
            </button>
            <a
              href="/login"
              className="ryze-header-action text-base px-4 py-2 transition-colors"
              style={{ color: 'rgba(240,237,230,0.7)' }}
              onMouseEnter={e => (e.currentTarget.style.color = '#F0EDE6')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(240,237,230,0.7)')}
            >
              {t('landing.actions.signIn')}
            </a>
            <a
              href="/signup"
              className="ryze-header-action ryze-header-action--primary text-base px-4 py-2 font-medium text-white"
              style={{ background: '#A2544F' }}
            >
              {t('landing.actions.startFreeTrial')}
            </a>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className={rtl ? 'col-start-3 justify-self-end p-2 lg:hidden' : 'p-2 md:hidden'}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{ color: '#F0EDE6' }}
            aria-label={t(mobileMenuOpen ? 'landing.accessibility.closeNavigation' : 'landing.accessibility.openNavigation')}
            aria-expanded={mobileMenuOpen}
            aria-controls="mobile-navigation"
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <nav
            id="mobile-navigation"
            className={`flex flex-col gap-2 px-4 pb-4 ${rtl ? 'lg:hidden' : 'md:hidden'}`}
            style={{ background: 'rgba(13,11,7,0.97)', borderBottom: '1px solid rgba(240,237,230,0.08)' }}
            aria-label={t('landing.accessibility.mobileNavigation')}
          >
            {NAV_LINKS.map(l => (
              <a key={l.key} href={l.href} className="py-2 text-base" style={{ color: 'rgba(240,237,230,0.7)' }}
                onClick={() => setMobileMenuOpen(false)}>
                {t(`landing.nav.${l.key}`)}
              </a>
            ))}
            <div className="pt-2 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => void i18n.changeLanguage(rtl ? 'en' : 'ar')}
                className="ryze-header-action inline-flex items-center justify-center gap-2 py-2 text-base"
                style={{ border: '1px solid rgba(240,237,230,0.15)', color: '#F0EDE6' }}
                aria-label={t(rtl ? 'common.switchToEnglish' : 'common.switchToArabic')}
              >
                <Languages className="h-4 w-4" aria-hidden="true" />
                {t(rtl ? 'common.english' : 'common.arabic')}
              </button>
              <a href="/login" className="ryze-header-action py-2 text-base text-center" style={{ border: '1px solid rgba(240,237,230,0.15)', color: '#F0EDE6' }}>
                {t('landing.actions.signIn')}
              </a>
              <a href="/signup" className="ryze-header-action ryze-header-action--primary py-2 text-base text-center font-medium text-white" style={{ background: '#A2544F' }}>
                {t('landing.actions.startFreeTrial')}
              </a>
            </div>
          </nav>
        )}
      </header>

      <main id="main-content">
      {/* ── HERO ──────────────────────────────────────────────── */}
      <section
        className="ryze-hero-section"
        aria-labelledby="landing-title"
        data-motion="hero"
      >
        <div className="ryze-hero-section__grid" aria-hidden="true" />
        <div className="ryze-hero-section__glow" data-motion="hero-glow" aria-hidden="true" />

        <div className="ryze-hero-layout">
          <div className={`ryze-hero-copy ${rtl ? 'text-right' : 'text-left'}`} data-motion="hero-copy">
          <a
            href="#integrations"
            className="ryze-hero-badge group min-h-10"
            data-motion="hero-badge"
          >
            <span className="ryze-hero-badge__status" aria-hidden="true" />
            <span className="ryze-hero-badge__count text-nowrap">{t('landing.hero.announcementBadge')}</span>
            <span className='text-nowrap'>{t('landing.hero.announcement')}</span>
            <DirectionalArrow className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
          </a>

          <h1
            id="landing-title"
            className="ryze-hero-title"
            aria-label={`${t('landing.hero.titleFirst')} ${t('landing.hero.titleHighlight')}`}
          >
            <span aria-hidden="true">
              <span className="ryze-hero-title__first" data-motion="hero-title-first">
                {t('landing.hero.titleFirst')}
              </span>
              <span
                key={rtl ? 'hero-title-highlight-rtl' : 'hero-title-highlight-ltr'}
                className="ryze-hero-title__highlight"
                data-motion="hero-title-highlight"
              >
                {t('landing.hero.titleHighlight')}
              </span>
            </span>
          </h1>

          <p
            className="ryze-hero-description"
            data-motion="hero-description"
          >
            {t('landing.hero.description')}
          </p>

          <div
            className="flex flex-col sm:flex-row items-stretch sm:items-center justify-start gap-3"
            data-motion="hero-actions"
          >
            <a
              href="/signup"
              className="ryze-hero-action ryze-hero-action--primary opacity-0 motion-reduce:opacity-100 will-change-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E8A09B] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D0B07]"
              data-motion-hero-button="primary"
            >
              {t('landing.actions.startTrial')}{' '}
              <DirectionalArrow className="h-4 w-4" data-motion-hero-button-arrow />
            </a>
            <a
              href="#integrations"
              className="ryze-hero-action ryze-hero-action--secondary opacity-0 motion-reduce:opacity-100 will-change-transform focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-[#0D0B07]"
              data-motion-hero-button="secondary"
            >
              {t('landing.actions.seeIntegrations')}
            </a>
          </div>

            <div className="ryze-hero-proof" data-motion="hero-reassurance">
              {['lineage', 'serverSide', 'currencies'].map(key => (
                <span key={key}>
                  <Check className="h-3.5 w-3.5" aria-hidden="true" />
                  {t(`landing.hero.proof.${key}`)}
                </span>
              ))}
            </div>
          </div>

          {/* ── Right: raw sources → unified model → live charts ── */}
          <div className="ryze-hero-visual" data-motion="hero-dashboard-shell">
            <div
              dir={rtl ? 'rtl' : 'ltr'}
              className="ryze-hero-pipeline"
              data-motion="hero-dashboard"
            >
              <header className="ryze-hero-pipeline__header">
                <div>
                  <span className="ryze-hero-pipeline__eyebrow">Ryze / data flow</span>
                  <strong>{t('landing.hero.visual.title')}</strong>
                </div>
                <span className="ryze-hero-live">
                  <i />
                  {t('landing.hero.visual.live')}
                </span>
              </header>

              <div className="ryze-hero-pipeline__body">
                <section className="ryze-hero-sources" aria-label={t('landing.hero.visual.rawSources')}>
                  <div className="ryze-hero-panel-label">
                    <span>01</span>
                    {t('landing.hero.visual.rawSources')}
                  </div>
                  <div className="ryze-hero-source-list">
                    {HERO_SOURCES.map((source, index) => (
                      <article
                        key={source.name}
                        className="ryze-hero-source-row"
                        data-motion="dashboard-metric"
                        style={{ '--source-color': source.color, '--source-delay': `${index * 0.45}s` } as CSSProperties}
                      >
                        <span className="ryze-hero-source-row__icon">{source.icon}</span>
                        <span className="ryze-hero-source-row__copy">
                          <strong>{source.name}</strong>
                          <small>{t(`landing.hero.visual.sources.${source.key}`)}</small>
                        </span>
                        <span className="ryze-hero-source-row__volume">{source.volume}</span>
                        <code dir="ltr">{source.schema}</code>
                        <span className="ryze-hero-source-row__pulse" aria-hidden="true" />
                      </article>
                    ))}
                  </div>
                </section>

                <section className="ryze-hero-model" aria-label={t('landing.hero.visual.unifiedLayer')}>
                  <div className="ryze-hero-panel-label">
                    <span>02</span>
                    {t('landing.hero.visual.unifiedLayer')}
                  </div>
                  <div className="ryze-hero-model__core">
                    <span className="ryze-hero-model__rings" aria-hidden="true" />
                    <Database className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <div className="ryze-hero-model__steps">
                    {['normalize', 'identity', 'attribute'].map(step => (
                      <span key={step}>
                        <Check className="h-3 w-3" />
                        {t(`landing.hero.visual.steps.${step}`)}
                      </span>
                    ))}
                  </div>
                  <div className="ryze-hero-model__status">
                    <span>{t('landing.hero.visual.mapped')}</span>
                    <strong>99.9%</strong>
                  </div>
                  <span className="ryze-hero-model__pulse" aria-hidden="true" />
                </section>

                <section className="ryze-hero-charts" aria-label={t('landing.hero.visual.liveCharts')}>
                  <div className="ryze-hero-charts__toolbar">
                    <div className="ryze-hero-panel-label">
                      <span>03</span>
                      {t('landing.hero.visual.liveCharts')}
                    </div>
                    <span className="ryze-hero-charts__currency">SAR</span>
                  </div>

                  <div className="ryze-hero-kpis">
                    {[
                      { key: 'revenue', value: '4.2m', delta: '+18.4%' },
                      { key: 'roas', value: '5.8×', delta: '+0.7×' },
                      { key: 'customers', value: '18.2k', delta: '+12.1%' },
                    ].map(metric => (
                      <article key={metric.key} className="ryze-hero-kpi" data-motion="dashboard-metric">
                        <span>{t(`landing.hero.visual.metrics.${metric.key}`)}</span>
                        <strong>{metric.value}</strong>
                        <small>{metric.delta}</small>
                      </article>
                    ))}
                  </div>

                  <div className="ryze-hero-chart-card">
                    <div className="ryze-hero-chart-card__header">
                      <span>{t('landing.hero.visual.revenueByChannel')}</span>
                      <small>{t('landing.hero.visual.today')}</small>
                    </div>
                    <div className="ryze-hero-channel-chart">
                      {HERO_CHANNELS.map(channel => (
                        <div key={channel.name} className="ryze-hero-channel-row">
                          <span>{channel.name}</span>
                          <div>
                            <i
                              data-motion="dashboard-chart-bar"
                              style={{ '--bar-width': channel.width } as CSSProperties}
                            />
                          </div>
                          <strong>{channel.value}</strong>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="ryze-hero-trend">
                    <div>
                      <span>{t('landing.hero.visual.customerTrend')}</span>
                      <strong>+24.6%</strong>
                    </div>
                    <div className="ryze-hero-trend__bars" aria-hidden="true">
                      {[36, 48, 42, 58, 52, 70, 66, 78, 74, 92].map((height, index) => (
                        <i key={index} style={{ height: `${height}%` }} />
                      ))}
                    </div>
                  </div>
                </section>
              </div>

              <footer className="ryze-hero-pipeline__footer">
                <span><i /> {t('landing.hero.visual.syncing')}</span>
                <span>{t('landing.hero.visual.rowsProcessed', { count: '12,482,901' })}</span>
                <span>{t('landing.hero.visual.reportingCurrency')}: SAR</span>
              </footer>
            </div>
            <div className="ryze-hero-visual__shadow" aria-hidden="true" />
          </div>
        </div>
      </section>

      {/* ── STATS ─────────────────────────────────────────────── */}
      <section className="py-12 px-4" style={{ borderTop: '1px solid rgba(240,237,230,0.06)', borderBottom: '1px solid rgba(240,237,230,0.06)' }}>
        <div
          className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8"
          data-motion-group="stats"
        >
          {STATS.map(s => (
            <div key={s.key} className="text-center" data-motion-item>
              <div className="text-3xl font-black mb-1" style={{ color: '#F0EDE6' }}>{'valueKey' in s ? t(`landing.stats.${s.valueKey}`) : s.value}</div>
              <div className="text-base" style={{ color: 'rgba(240,237,230,0.52)' }}>{t(`landing.stats.${s.key}`)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── FEATURES ──────────────────────────────────────────── */}
      <section id="features" className="ryze-features-section px-4 py-24 md:py-32" aria-labelledby="features-title">
        <div className="ryze-features-section__glow" aria-hidden="true" />
        <div className="max-w-6xl mx-auto relative">
          <div
            className="grid gap-8 md:grid-cols-[minmax(0,1.45fr)_minmax(280px,0.55fr)] md:items-end mb-12 md:mb-16"
            data-motion="section-heading"
          >
            <div>
              <div className="inline-flex items-center gap-3 mb-5">
                <span className="h-px w-8" style={{ background: '#A2544F' }} aria-hidden="true" />
                <span className="text-sm font-bold uppercase tracking-[0.18em]" style={{ color: '#C97872' }}>
                  {t('landing.sections.features.label')}
                </span>
              </div>
              <h2
                id="features-title"
                className="text-4xl sm:text-5xl md:text-6xl font-black tracking-[-0.045em] leading-[0.98] text-balance"
                style={{ color: '#F0EDE6' }}
              >
                {t('landing.sections.features.titleFirst')}{' '}
                <span style={{ color: 'rgba(240,237,230,0.48)' }}>
                  {t('landing.sections.features.titleSecond')}
                </span>
              </h2>
            </div>
            <div className={rtl ? 'md:text-right' : 'md:text-left'}>
              <p className="text-base md:text-lg leading-relaxed mb-5" style={{ color: 'rgba(240,237,230,0.64)' }}>
                {t('landing.sections.features.description')}
              </p>
              <div
                className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold uppercase tracking-[0.1em]"
                style={{
                  color: 'rgba(240,237,230,0.72)',
                  background: 'rgba(240,237,230,0.045)',
                  border: '1px solid rgba(240,237,230,0.09)',
                }}
              >
                <span className="h-1.5 w-1.5 rounded-full" style={{ background: '#6EBF8B', boxShadow: '0 0 10px rgba(110,191,139,0.75)' }} />
                {t('landing.sections.features.systemStatus')}
              </div>
            </div>
          </div>
          <div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4"
            data-motion-group="features"
          >
            {FEATURES.map((f, index) => {
              const featured = 'featured' in f && f.featured;

              return (
                <article
                  key={f.key}
                  className={`ryze-feature-card ${featured ? 'ryze-feature-card--featured lg:col-span-3' : 'lg:col-span-2'}`}
                  data-motion-item
                >
                  <div className="ryze-feature-card__accent" aria-hidden="true" />
                  <div className="relative z-10 flex h-full flex-col">
                    <div className="flex items-start justify-between gap-5 mb-8">
                      <div className="ryze-feature-card__icon">
                        {f.icon}
                      </div>
                      <span className="font-mono text-xs tracking-[0.16em]" style={{ color: 'rgba(240,237,230,0.34)' }}>
                        {String(index + 1).padStart(2, '0')}
                      </span>
                    </div>

                    <div className="mb-7">
                      <h3
                        className={`${featured ? 'text-xl md:text-2xl' : 'text-lg'} font-bold tracking-[-0.02em] mb-3 leading-tight`}
                        style={{ color: '#F0EDE6' }}
                      >
                        {t(`landing.features.${f.key}.title`)}
                      </h3>
                      <p
                        className={`${featured ? 'max-w-xl' : ''} text-base leading-7`}
                        style={{ color: 'rgba(240,237,230,0.62)' }}
                      >
                        {t(`landing.features.${f.key}.description`)}
                      </p>
                    </div>

                    <FeatureSignal
                      featureKey={f.key}
                      signal={t(`landing.features.${f.key}.signal`)}
                      featured={featured}
                    />
                  </div>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── INTEGRATIONS ──────────────────────────────────────── */}
      <section id="integrations" className="ryze-integrations-section px-4 py-24 md:py-32" aria-labelledby="integrations-title">
        <div className="ryze-integrations-section__glow" aria-hidden="true" />
        <div className="max-w-6xl mx-auto relative grid gap-12 lg:grid-cols-[minmax(0,0.72fr)_minmax(0,1.28fr)] lg:gap-16 lg:items-center">
          <div data-motion="section-heading">
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="h-px w-8" style={{ background: '#A2544F' }} aria-hidden="true" />
              <span className="text-sm font-bold uppercase tracking-[0.18em]" style={{ color: '#C97872' }}>
                {t('landing.sections.integrations.label')}
              </span>
            </div>
            <h2
              id="integrations-title"
              className="text-4xl sm:text-5xl md:text-6xl font-black tracking-[-0.045em] leading-[0.98] text-balance mb-6"
              style={{ color: '#F0EDE6' }}
            >
              {t('landing.sections.integrations.title')}
            </h2>
            <p className="text-base md:text-lg leading-relaxed max-w-xl" style={{ color: 'rgba(240,237,230,0.64)' }}>
              {t('landing.sections.integrations.description')}
            </p>

            <div className="grid grid-cols-2 gap-3 mt-9 max-w-md" aria-label={t('landing.sections.integrations.summaryLabel')}>
              <div className="ryze-integration-stat">
                <strong>18+</strong>
                <span>{t('landing.sections.integrations.nativeConnectors')}</span>
              </div>
              <div className="ryze-integration-stat">
                <strong>{t('landing.sections.integrations.anySourceValue')}</strong>
                <span>{t('landing.sections.integrations.anySourceLabel')}</span>
              </div>
            </div>
          </div>

          <div
            className="ryze-integrations-board"
            data-motion-group="integrations"
          >
            <div className="ryze-integrations-board__header" data-motion-item>
              <div className="inline-flex items-center gap-2">
                <span className="ryze-integrations-board__status" aria-hidden="true" />
                <span>{t('landing.sections.integrations.networkStatus')}</span>
              </div>
              <span dir="ltr" className="font-mono text-xs tracking-[0.14em]" style={{ color: 'rgba(240,237,230,0.4)' }}>
                RYZE / CONNECT
              </span>
            </div>

            <div className="ryze-integrations-board__grid">
              {INTEGRATION_GROUPS.map(group => (
                <article key={group.key} className="ryze-integration-group" data-motion-item>
                  <div className="flex items-start justify-between gap-4 mb-5">
                    <div className="flex items-center gap-2.5">
                      <span className="ryze-integration-group__icon" aria-hidden="true">
                        {group.icon}
                      </span>
                      <h3 className="text-sm font-bold" style={{ color: '#F0EDE6' }}>
                        {t(`landing.sections.integrations.categories.${group.key}`)}
                      </h3>
                    </div>
                    <span className="font-mono text-xs" style={{ color: 'rgba(240,237,230,0.4)' }}>
                      {String(group.items.length).padStart(2, '0')}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {group.items.map(item => (
                      <span key={item.name} className="ryze-integration-chip">
                        <span
                          className="ryze-integration-chip__dot"
                          style={{ background: item.color, boxShadow: `0 0 10px ${item.color}55` }}
                          aria-hidden="true"
                        />
                        {item.name}
                      </span>
                    ))}
                  </div>
                </article>
              ))}
            </div>

            <div className="ryze-integrations-board__custom" data-motion-item>
              <span className="ryze-integrations-board__plus" aria-hidden="true">
                <Plus className="h-5 w-5" />
              </span>
              <div>
                <div className="text-sm font-bold mb-1" style={{ color: '#F0EDE6' }}>
                  {t('landing.sections.integrations.customSources')}
                </div>
                <p className="text-sm leading-relaxed" style={{ color: 'rgba(240,237,230,0.56)' }}>
                  {t('landing.sections.integrations.customDescription')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── WHY RYZE ──────────────────────────────────────────── */}
      <section className="ryze-why-section px-4 py-24 md:py-32" aria-labelledby="why-ryze-title">
        <div className="ryze-why-section__glow" aria-hidden="true" />
        <div className="max-w-6xl mx-auto relative">
          <div
            className="grid gap-8 md:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.65fr)] md:items-end mb-12 md:mb-16"
            data-motion="section-heading"
          >
            <div>
              <div className="inline-flex items-center gap-3 mb-5">
                <span className="h-px w-8" style={{ background: '#A2544F' }} aria-hidden="true" />
                <span className="text-sm font-bold uppercase tracking-[0.18em]" style={{ color: '#C97872' }}>
                  {t('landing.sections.why.label')}
                </span>
              </div>
              <h2
                id="why-ryze-title"
                className="text-4xl sm:text-5xl md:text-6xl font-black tracking-[-0.045em] leading-[0.98] text-balance"
                style={{ color: '#F0EDE6' }}
              >
                {t('landing.sections.why.title')}
              </h2>
            </div>
            <div>
              <p className="text-base md:text-lg leading-relaxed mb-5" style={{ color: 'rgba(240,237,230,0.64)' }}>
                {t('landing.sections.why.description')}
              </p>
              <div
                className="inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold uppercase tracking-[0.08em]"
                style={{
                  color: 'rgba(240,237,230,0.7)',
                  background: 'rgba(240,237,230,0.04)',
                  border: '1px solid rgba(240,237,230,0.085)',
                }}
              >
                <Check className="h-3.5 w-3.5" style={{ color: '#6EBF8B' }} aria-hidden="true" />
                {t('landing.sections.why.decisionStatus')}
              </div>
            </div>
          </div>

          <div
            className="ryze-why-list"
            data-motion-group="differentiators"
          >
            {DIFFERENTIATORS.map((d, index) => (
              <article
                key={d.key}
                className="ryze-why-row"
                data-motion-item
              >
                <span className="ryze-why-row__index" aria-hidden="true">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <span className="ryze-why-row__icon" aria-hidden="true">
                  {d.icon}
                </span>
                <h3 className="ryze-why-row__title">
                  {t(`landing.differentiators.${d.key}.title`)}
                </h3>
                <p className="ryze-why-row__description">
                  {t(`landing.differentiators.${d.key}.description`)}
                </p>
                <div className="ryze-why-row__signal">
                  <span className="ryze-why-row__signal-dot" aria-hidden="true" />
                  {t(`landing.differentiators.${d.key}.signal`)}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ───────────────────────────────────────────── */}
      <section id="pricing" className="ryze-pricing-section px-4 py-24 md:py-32" aria-labelledby="pricing-title">
        <div className="ryze-pricing-section__glow" aria-hidden="true" />
        <div className="max-w-6xl mx-auto relative">
          <div
            className="grid gap-8 md:grid-cols-[minmax(0,1.35fr)_minmax(280px,0.65fr)] md:items-end mb-12 md:mb-16"
            data-motion="section-heading"
          >
            <div>
              <div className="inline-flex items-center gap-3 mb-5">
                <span className="h-px w-8" style={{ background: '#A2544F' }} aria-hidden="true" />
                <span className="text-sm font-bold uppercase tracking-[0.18em]" style={{ color: '#C97872' }}>
                  {t('landing.sections.pricing.label')}
                </span>
              </div>
              <h2
                id="pricing-title"
                className="text-4xl sm:text-5xl md:text-6xl font-black tracking-[-0.045em] leading-[0.98] text-balance"
                style={{ color: '#F0EDE6' }}
              >
                {t('landing.sections.pricing.title')}
              </h2>
            </div>
            <div>
              <p className="text-base md:text-lg leading-relaxed mb-5" style={{ color: 'rgba(240,237,230,0.64)' }}>
                {t('landing.sections.pricing.description')}
              </p>
              <div className="ryze-pricing-trust">
                <Check className="h-3.5 w-3.5" aria-hidden="true" />
                {t('landing.sections.pricing.trialStatus')}
              </div>
            </div>
          </div>

          <div
            className="ryze-pricing-grid"
            data-motion-group="pricing"
          >
            {PLANS.filter(plan => plan.key !== 'enterprise').map(plan => (
              <article
                key={plan.key}
                className={`ryze-pricing-card ${plan.popular ? 'ryze-pricing-card--popular' : ''}`}
                data-motion-item
              >
                <div className="ryze-pricing-card__accent" aria-hidden="true" />
                {plan.popular && (
                  <div className="ryze-pricing-card__popular">
                    {t('landing.plans.mostPopular')}
                  </div>
                )}

                <div className="mb-5">
                  <h3 className="text-xl font-bold tracking-[-0.025em] mb-2" style={{ color: '#F0EDE6' }}>
                    {t(`landing.plans.${plan.key}.name`)}
                  </h3>
                  <p className="text-base leading-relaxed min-h-12" style={{ color: 'rgba(240,237,230,0.58)' }}>
                    {t(`landing.plans.${plan.key}.description`)}
                  </p>
                </div>

                <div className="ryze-pricing-card__price">
                  {plan.price !== null ? (
                    <div dir="ltr" className={`flex items-baseline gap-1 ${rtl ? 'justify-end' : 'justify-start'}`}>
                      <span className="text-4xl font-black tracking-[-0.045em]" style={{ color: '#F0EDE6' }}>${plan.price}</span>
                      <span className="text-sm" style={{ color: 'rgba(240,237,230,0.48)' }}>/{t('landing.plans.month')}</span>
                    </div>
                  ) : (
                    <div className="text-2xl font-black" style={{ color: '#F0EDE6' }}>{t('landing.plans.custom')}</div>
                  )}
                </div>

                <ul className="ryze-pricing-card__features">
                  {plan.featureKeys.map(feature => (
                    <li key={feature}>
                      <span className="ryze-pricing-card__check" aria-hidden="true">
                        <Check className="h-3 w-3" />
                      </span>
                      {t(`landing.plans.${plan.key}.features.${feature}`)}
                    </li>
                  ))}
                </ul>

                <a
                  href={plan.price !== null ? '/signup' : '/contact?subject=Ryze%20Enterprise%20enquiry'}
                  className="ryze-pricing-card__action group"
                >
                  {t(plan.key === 'enterprise' ? 'landing.actions.talkToSales' : 'landing.actions.startFreeTrial')}
                  <DirectionalArrow className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                </a>
              </article>
            ))}

            {PLANS.filter(plan => plan.key === 'enterprise').map(plan => (
              <article key={plan.key} className="ryze-pricing-enterprise" data-motion-item>
                <div className="ryze-pricing-enterprise__intro">
                  <div className="flex items-center gap-3 mb-4">
                    <span className="ryze-pricing-card__index">04</span>
                    <span className="ryze-pricing-card__type">{t('landing.sections.pricing.enterprise')}</span>
                  </div>
                  <h3 className="text-2xl font-bold tracking-[-0.03em] mb-2" style={{ color: '#F0EDE6' }}>
                    {t(`landing.plans.${plan.key}.name`)}
                  </h3>
                  <p className="text-base leading-relaxed" style={{ color: 'rgba(240,237,230,0.6)' }}>
                    {t(`landing.plans.${plan.key}.description`)}
                  </p>
                </div>

                <ul className="ryze-pricing-enterprise__features">
                  {plan.featureKeys.map(feature => (
                    <li key={feature}>
                      <Check className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                      {t(`landing.plans.${plan.key}.features.${feature}`)}
                    </li>
                  ))}
                </ul>

                <div className="ryze-pricing-enterprise__action-wrap">
                  <div className="text-2xl font-black mb-4" style={{ color: '#F0EDE6' }}>
                    {t('landing.plans.custom')}
                  </div>
                  <a href="/contact?subject=Ryze%20Enterprise%20enquiry" className="ryze-pricing-card__action group">
                    {t('landing.actions.talkToSales')}
                    <DirectionalArrow className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                  </a>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-8 text-center" data-motion="section-heading">
            <a
              href="/pricing#compare"
              className="ryze-pricing-compare group"
            >
              {t('landing.actions.compareAllPlans')}
              <DirectionalArrow className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      {/* ── CHANGELOG TEASER ──────────────────────────────────── */}
      <section id="changelog" className="ryze-changelog-section px-4 py-24 md:py-32" aria-labelledby="changelog-title">
        <div className="ryze-changelog-section__glow" aria-hidden="true" />
        <div className="max-w-6xl mx-auto relative grid gap-12 lg:grid-cols-[minmax(0,0.68fr)_minmax(0,1.32fr)] lg:gap-16 lg:items-center">
          <div data-motion="section-heading">
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="h-px w-8" style={{ background: '#A2544F' }} aria-hidden="true" />
              <span className="text-sm font-bold uppercase tracking-[0.18em]" style={{ color: '#C97872' }}>
                {t('landing.sections.changelog.label')}
              </span>
            </div>
            <h2
              id="changelog-title"
              className="text-4xl sm:text-5xl md:text-6xl font-black tracking-[-0.045em] leading-[0.98] text-balance mb-6"
              style={{ color: '#F0EDE6' }}
            >
              {t('landing.sections.changelog.title')}
            </h2>
            <p className="text-base md:text-lg leading-relaxed max-w-lg" style={{ color: 'rgba(240,237,230,0.64)' }}>
              {t('landing.sections.changelog.description')}
            </p>
            <div className="ryze-changelog-summary">
              <strong>03</strong>
              <span>{t('landing.sections.changelog.latestReleases')}</span>
            </div>
          </div>

          <div className="ryze-changelog-board" data-motion-group="changelog">
            <div className="ryze-changelog-board__header" data-motion-item>
              <div className="inline-flex items-center gap-2">
                <span className="ryze-changelog-board__status" aria-hidden="true" />
                {t('landing.sections.changelog.shippingStatus')}
              </div>
              <span dir="ltr" className="font-mono text-xs tracking-[0.13em]" style={{ color: 'rgba(240,237,230,0.4)' }}>
                RELEASE / LOG
              </span>
            </div>

            {CHANGELOG_ENTRIES.map(entry => (
              <article
                key={entry.key}
                className="ryze-changelog-entry"
                data-motion-item
              >
                <div className="ryze-changelog-entry__meta">
                  <span dir="ltr" className="ryze-changelog-entry__version">{entry.version}</span>
                  <span className="ryze-changelog-entry__category">
                    {t(`landing.changelog.${entry.key}.category`)}
                  </span>
                  <time dateTime={entry.dateTime}>
                    {t(`landing.changelog.${entry.key}.date`)}
                  </time>
                </div>
                <div>
                  <h3 className="text-xl font-bold mb-2" style={{ color: '#F0EDE6' }}>
                    {t(`landing.changelog.${entry.key}.title`)}
                  </h3>
                  <p className="text-base leading-relaxed" style={{ color: 'rgba(240,237,230,0.6)' }}>
                    {t(`landing.changelog.${entry.key}.description`)}
                  </p>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ───────────────────────────────────────────────── */}
      <section id="faq" className="ryze-faq-section px-4 py-24 md:py-32" aria-labelledby="faq-title">
        <div className="ryze-faq-section__glow" aria-hidden="true" />
        <div className="max-w-6xl mx-auto relative grid gap-12 lg:grid-cols-[minmax(0,0.66fr)_minmax(0,1.34fr)] lg:gap-16">
          <div data-motion="section-heading">
            <div className="inline-flex items-center gap-3 mb-5">
              <span className="h-px w-8" style={{ background: '#A2544F' }} aria-hidden="true" />
              <span className="text-sm font-bold uppercase tracking-[0.18em]" style={{ color: '#C97872' }}>
                {t('landing.sections.faq.label')}
              </span>
            </div>
            <h2
              id="faq-title"
              className="text-4xl sm:text-5xl md:text-6xl font-black tracking-[-0.045em] leading-[0.98] text-balance mb-6"
              style={{ color: '#F0EDE6' }}
            >
              {t('landing.sections.faq.title')}
            </h2>
            <p className="text-base md:text-lg leading-relaxed max-w-lg" style={{ color: 'rgba(240,237,230,0.64)' }}>
              {t('landing.sections.faq.description')}
            </p>

            <div className="ryze-faq-support">
              <div>
                <div className="text-base font-bold mb-1" style={{ color: '#F0EDE6' }}>
                  {t('landing.sections.faq.supportTitle')}
                </div>
                <p className="text-base leading-relaxed" style={{ color: 'rgba(240,237,230,0.56)' }}>
                  {t('landing.sections.faq.supportDescription')}
                </p>
              </div>
              <a href="/contact" className="group">
                {t('landing.actions.talkToSales')}
                <DirectionalArrow className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
              </a>
            </div>
          </div>

          <div className="ryze-faq-list" data-motion-group="faq">
            {FAQS.map((key, index) => {
              const isOpen = openFaq === key;
              const questionId = `faq-question-${key}`;
              const answerId = `faq-answer-${key}`;

              return (
                <div
                  key={key}
                  className="ryze-faq-item"
                  data-open={isOpen}
                  data-motion-item
                >
                  <button
                    type="button"
                    id={questionId}
                    className="ryze-faq-item__trigger"
                    onClick={() => setOpenFaq(current => current === key ? null : key)}
                    aria-expanded={isOpen}
                    aria-controls={answerId}
                  >
                  <span className="ryze-faq-item__index" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  <span className="ryze-faq-item__question">
                    {t(`landing.faq.${key}.question`)}
                  </span>
                  <span className="ryze-faq-item__toggle" aria-hidden="true">
                    <Plus className="h-4 w-4" />
                  </span>
                  </button>
                  <div
                    id={answerId}
                    className="ryze-faq-item__collapse"
                    role="region"
                    aria-labelledby={questionId}
                    aria-hidden={!isOpen}
                  >
                    <div className="ryze-faq-item__collapse-inner">
                      <div className="ryze-faq-item__answer">
                        {t(`landing.faq.${key}.answer`)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── CTA ───────────────────────────────────────────────── */}
      <section className="py-24 px-4" style={{ borderTop: '1px solid rgba(240,237,230,0.06)' }}>
        <div className="max-w-2xl mx-auto text-center" data-motion-group="cta">
          <h2 className="text-4xl md:text-5xl font-black tracking-tight mb-6" style={{ color: '#F0EDE6' }} data-motion-item>
            {t('landing.cta.title')}
          </h2>
          <p className="text-base mb-10" style={{ color: 'rgba(240,237,230,0.55)' }} data-motion-item>
            {t('landing.cta.description')}
          </p>
          <a
            href="/signup"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-bold text-white transition-all hover:opacity-90 hover:scale-105 text-base"
            style={{ background: '#A2544F' }}
            data-motion-item
          >
            {t('landing.cta.action')} <DirectionalArrow className="h-5 w-5" />
          </a>
          <p className="mt-4 text-sm" style={{ color: 'rgba(240,237,230,0.42)' }} data-motion-item>{t('landing.cta.reassurance')}</p>
        </div>
      </section>
      </main>

      {/* ── FOOTER ────────────────────────────────────────────── */}
      <MarketingFooter />

      <button
        type="button"
        onClick={scrollToTop}
        className={`fixed bottom-5 sm:bottom-6 ${rtl ? 'left-5 sm:left-6' : 'right-5 sm:right-6'} z-[55] inline-flex h-11 w-11 sm:h-auto sm:w-auto sm:px-4 sm:py-3 items-center justify-center gap-2 rounded-full text-sm font-semibold transition-all duration-300 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 ${
          showBackToTop
            ? 'translate-y-0 opacity-100'
            : 'pointer-events-none translate-y-3 opacity-0'
        }`}
        style={{
          background: 'rgba(22,18,13,0.94)',
          border: '1px solid rgba(240,237,230,0.16)',
          color: '#F0EDE6',
          boxShadow: '0 12px 32px rgba(0,0,0,0.38)',
          backdropFilter: 'blur(12px)',
        }}
        aria-label={t('landing.actions.backToTop')}
        title={t('landing.actions.backToTop')}
        tabIndex={showBackToTop ? 0 : -1}
      >
        <ArrowUp className="h-4 w-4" aria-hidden="true" />
        <span className="hidden sm:inline">{t('landing.actions.backToTop')}</span>
      </button>
    </div>
  );
}
